import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  AssignmentCandidate,
  CreateJobInput,
  JobDetail,
  JobListItem,
  JobStatusValue,
  RescheduleJobInput,
  UpdateJobInput,
} from '@shared/schemas/job';
import type { ListResponse, RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/jobs and /admin/calendar. ROADMAP.md §4.3–4.7. */

const ROOT = ['admin', 'jobs'] as const;

/** The badge tone for each status — the one mapping, so no screen invents its own. */
export const JOB_STATUS_TONES: Record<JobStatusValue, StatusTone> = {
  pending: 'pending',
  scheduled: 'scheduled',
  in_progress: 'in-progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

function invalidate(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: ROOT });
}

export interface JobListParams {
  sort: RecordSort;
  page: number;
  search: string;
  status: JobStatusValue | 'all';
  /**
   * Scopes the list to one cleaner's crew memberships — the Job history tab on
   * `/admin/employees/:id`. The server has taken this since 4.2; nothing on the
   * browser side had ever passed it.
   */
  employeeId?: string;
}

export function useJobs(params: JobListParams) {
  return useQuery({
    queryKey: [...ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<JobListItem>>(
        `/api/admin/jobs${queryString({
          page: params.page,
          sort: params.sort,
          search: params.search,
          status: params.status === 'all' ? undefined : params.status,
          employeeId: params.employeeId,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

/**
 * The calendar's read: everything in the visible window, nothing more (P8).
 * Keyed by the range, so paging a month back is its own cache entry.
 */
/** The calendar's two filters. Both absent is the whole calendar. */
export interface CalendarFilters {
  clientId?: string;
  highPriorityOnly?: boolean;
}

export function useJobsInRange(from: string, to: string, filters: CalendarFilters = {}) {
  return useQuery({
    // The filters are part of the key, so switching client keeps the previous
    // month on screen rather than showing another client's jobs for an instant.
    queryKey: [...ROOT, 'range', from, to, filters],
    queryFn: () =>
      api.get<{ data: JobListItem[] }>(
        `/api/admin/jobs/calendar${queryString({
          from,
          to,
          clientId: filters.clientId,
          // Sent only when on. An explicit `false` would work too — the schema
          // reads the string — but a URL that says nothing when nothing is
          // filtered is the one somebody can read.
          highPriorityOnly: filters.highPriorityOnly === true ? 'true' : undefined,
        })}`,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, 'detail', id],
    queryFn: () => api.get<JobDetail>(`/api/admin/jobs/${id ?? ''}`),
    enabled: id !== undefined,
  });
}

export function useCreateJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJobInput) => api.post<JobDetail>('/api/admin/jobs', input),
    onSuccess: () => invalidate(client),
  });
}

export function useUpdateJob(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateJobInput) => api.patch<JobDetail>(`/api/admin/jobs/${id}`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useRescheduleJob(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RescheduleJobInput) =>
      api.post<JobDetail>(`/api/admin/jobs/${id}/reschedule`, input),
    onSuccess: () => invalidate(client),
  });
}

export function useCancelJob(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) => api.post<JobDetail>(`/api/admin/jobs/${id}/cancel`, { reason }),
    onSuccess: () => invalidate(client),
  });
}

export function useStartJob(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<JobDetail>(`/api/admin/jobs/${id}/start`),
    onSuccess: () => invalidate(client),
  });
}

export function useCompleteJob(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (completedAt?: string) =>
      api.post<JobDetail>(
        `/api/admin/jobs/${id}/complete`,
        completedAt === undefined ? {} : { completedAt },
      ),
    onSuccess: () => invalidate(client),
  });
}

export function useSetAssignments(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (employeeIds: string[]) =>
      api.put<JobDetail>(`/api/admin/jobs/${id}/assignments`, { employeeIds }),
    onSuccess: () => invalidate(client),
  });
}

export function useSetTaskCompletion(jobId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; isCompleted: boolean }) =>
      api.put<JobDetail>(`/api/admin/jobs/${jobId}/tasks/${input.taskId}/completion`, {
        isCompleted: input.isCompleted,
      }),
    onSuccess: () => invalidate(client),
  });
}

export interface AssignmentCheckParams {
  date: string;
  time: string;
  durationMinutes: number;
  excludeJobId?: string;

  /**
   * Both feed the §4 scoring only — history with this client, and the skills
   * and position the service wants. Optional because "who is free on Thursday
   * morning" is a legitimate question before a client has been chosen; where
   * one is missing the ranking says so rather than scoring it silently.
   */
  clientId?: string;
  serviceTypeId?: string;
  /** Which address the job is at, for the same-area score — business-rules.md §4. */
  propertyId?: string;
}

/**
 * Who could take the job in this window, **ranked, with the reasons**. Enabled
 * only once the dialog has a real date and time — asking for candidates for a
 * blank window would be a 400 that renders as an error nobody caused.
 */
export function useAssignmentCandidates(params: AssignmentCheckParams | null) {
  return useQuery({
    queryKey: [...ROOT, 'candidates', params],
    queryFn: () =>
      api.get<{ data: AssignmentCandidate[] }>(
        `/api/admin/jobs/assignment-candidates${queryString({
          date: params?.date,
          time: params?.time,
          durationMinutes: params?.durationMinutes,
          excludeJobId: params?.excludeJobId,
          clientId: params?.clientId,
          serviceTypeId: params?.serviceTypeId,
          propertyId: params?.propertyId,
        })}`,
      ),
    enabled: params !== null,
    placeholderData: (previous) => previous,
  });
}
